import { Field, FieldProps, UiSchema } from "@rjsf/utils";
import React, { useContext, useEffect, useRef, useState } from "react";

import { WaitlistContext } from "../context/WaitlistContext";
import { isZipCodeAllowed, parseZipCodes } from "../helper/ZipCodeHelper";
import { WAITLIST_COLOR } from "../widget/LimitedCheckboxWidget";

const ERROR_COLOR = "#dc3545";

const DEFAULT_ERROR_MESSAGE = "Eine Anmeldung von Ihrem Wohnort ist nicht möglich.";
// Own classes and fixed colors: host pages (and the portal theme) restyle `.form-text` with !important.
const messageStyle = (color: string): React.CSSProperties => ({ color, fontSize: "0.875em", marginTop: 4 });
// Swiss zip codes have four digits; show the message once the input is complete or the field was left.
const COMPLETE_ZIP_CODE_LENGTH = 4;

const getErrorMessage = (uiSchema: UiSchema | undefined): string => {
  const message = uiSchema?.["ui:options"]?.zipCodeErrorMessage;
  return typeof message === "string" && message.trim() !== "" ? message : DEFAULT_ERROR_MESSAGE;
};

export const ZipCodeField: Field = (props: FieldProps) => {
  const [fieldValue, setFieldValue] = useState<string | undefined>(props.formData as string | undefined);
  const [touched, setTouched] = useState(false);
  const ref = useRef<HTMLInputElement>(null);
  // Tracks whether fieldValue originates from user input. Emitting onChange for the prefilled
  // (edit) value on mount feeds it back into rjsf and re-renders the form in a loop; only real
  // edits should propagate.
  const userEdited = useRef(false);
  const allowedZipCodes = parseZipCodes(props.uiSchema?.["ui:options"]?.zipCodes);
  const errorMessage = getErrorMessage(props.uiSchema);
  const waitlist = useContext(WaitlistContext);

  const isAllowed = fieldValue === undefined || isZipCodeAllowed(fieldValue, allowedZipCodes);
  const showMessage = !isAllowed && (touched || (fieldValue?.trim().length ?? 0) >= COMPLETE_ZIP_CODE_LENGTH);

  useEffect(() => {
    // Only propagate user edits — never the initial/prefilled value synced from props.formData.
    if (!userEdited.current || fieldValue === undefined) return;
    if (isAllowed || waitlist.enabled) {
      // with an active waitlist a zip code outside the list is kept: the registration goes to the waitlist
      ref.current?.setCustomValidity("");
      props.onChange(fieldValue, props.fieldPathId.path);
    } else {
      ref.current?.setCustomValidity(errorMessage);
      props.onChange(undefined, props.fieldPathId.path);
    }
  }, [fieldValue]);

  return (
    <div className={"d-flex flex-wrap mx-0 my-0"}>
      <div className={"w-100 mb-0"}>
        <label htmlFor={props.name} className="form-label">
          {props.name}
        </label>
        <input
          ref={ref}
          type={"text"}
          id={props.name}
          name={props.name}
          defaultValue={fieldValue}
          required={props.required}
          className={"form-control"}
          onBlur={() => setTouched(true)}
          onChange={(e) => {
            userEdited.current = true;
            setFieldValue(e.target.value);
          }}
        />
        {showMessage &&
          (waitlist.enabled ? (
            <div className={"zip-code-hint"} style={messageStyle(WAITLIST_COLOR)}>
              {waitlist.zipCodeMessage}
            </div>
          ) : (
            <div className={"zip-code-error"} style={messageStyle(ERROR_COLOR)}>
              {errorMessage}
            </div>
          ))}
      </div>
    </div>
  );
};
